import React, { useEffect, useState } from 'react'
import { Link, useLocation, useNavigate } from 'react-router-dom';
import Select from 'react-select';
import toast from 'react-hot-toast';
import withReactContent from 'sweetalert2-react-content';
import Swal from 'sweetalert2';
import ContentLoader from '../loader/ContentLoader';
import { vendorsService } from '../../services/vendors.service';
import { pincodeService } from '../../services/pincode.service';
import { SOMETHING_WENT_WRONG } from '../../constants/strings';
import { formField, formInputStyle, formLabelStyle, formReadOnlyStyle, formSectionCard, formSectionTitle, formSelectStyles } from "../common/FormUI";

const GRAD = "linear-gradient(135deg, var(--btn-bg) 0%, var(--btn-hover-bg) 100%)";

const STATUS_OPTIONS = [
  { value: "active", label: "Active" },
  { value: "inactive", label: "Inactive" },
];

const initialVendor = {
  vendor_code: "",
  company_name: "",
  executive: "",
  mobile: "",
  email: "",
  address: "",
  pincode: "",
  city: "",
  state: "",
  country: "India",
  status: "active",
};

const AddVendor = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const editVendorId = location.state?.vendorId;
  const isEdit = !!editVendorId;

  const [loading, setLoading] = useState(false);
  const [vendor, setVendor] = useState(initialVendor);
  // city/state options returned by the pincode lookup (a pincode can map to several)
  const [cityOptions, setCityOptions] = useState([]);
  const [stateOptions, setStateOptions] = useState([]);

  useEffect(() => {
    if (isEdit) loadVendorDetail();
  }, [editVendorId]);

  const showSwal = (title, text, icon) => {
    withReactContent(Swal).fire({ title, text, icon })
  }

  const loadVendorDetail = async () => {
    try {
      setLoading(true);
      const res = await vendorsService.details(editVendorId);
      if (res?.success) {
        const v = res.resData?.vendor ?? res.resData;
        setVendor({
          vendor_code: v?.vendor_code || "",
          company_name: v?.company_name || "",
          executive: v?.executive || "",
          mobile: v?.mobile || "",
          email: v?.email || "",
          address: v?.address || "",
          pincode: v?.pincode || "",
          city: v?.city || "",
          state: v?.state || "",
          country: v?.country || "",
          status: v?.status || "active",
        });
      } else {
        toast.error(res?.message);
      }
    } catch (err) {
      showSwal("Error", SOMETHING_WENT_WRONG, "error")
    } finally {
      setLoading(false);
    }
  }

  const handleChange = (e) => {
    setVendor({ ...vendor, [e.target.name]: e.target.value });
  }

  const handleMobileChange = (e) => {
    setVendor({ ...vendor, mobile: e.target.value.replace(/\D/g, "").slice(0, 10) });
  }

  const handlePincodeChange = async (e) => {
    const pin = e.target.value.replace(/\D/g, "").slice(0, 6);
    setVendor((v) => ({ ...v, pincode: pin }));
    if (pin.length !== 6) return;
    try {
      const res = await pincodeService.lookup(pin);
      if (res?.success) {
        const cities = res.resData?.cities || [];
        const states = res.resData?.states || [];
        const countries = res.resData?.countries || [];
        setCityOptions(cities.map(c => ({ value: c, label: c })));
        setStateOptions(states.map(s => ({ value: s, label: s })));
        setVendor((v) => ({
          ...v,
          pincode: pin,
          city: cities[0] || v.city,
          state: states[0] || v.state,
          country: countries[0] || v.country,
        }));
      } else {
        toast.error(res?.message);
      }
    } catch (err) {
      showSwal("Error", SOMETHING_WENT_WRONG, "error")
    }
  }

  const validate = () => {
    if (!vendor.company_name.trim()) return "Company name is required";
    if (!vendor.executive.trim()) return "Executive name is required";
    if (!/^\d{10}$/.test(vendor.mobile)) return "Enter a valid 10 digit mobile number";
    if (!/^\S+@\S+\.\S+$/.test(vendor.email)) return "Enter a valid email address";
    if (!vendor.address.trim()) return "Address is required";
    if (!/^\d{6}$/.test(vendor.pincode)) return "Enter a valid 6 digit pincode";
    if (!vendor.city.trim() || !vendor.state.trim() || !vendor.country.trim()) return "City, state and country are required";
    return null;
  }

  const handleSubmit = async (e) => {
    e.preventDefault();
    const err = validate();
    if (err) {
      toast.error(err);
      return;
    }
    const confirm = await withReactContent(Swal).fire({
      title: isEdit ? "Update vendor?" : "Save vendor?",
      icon: "question",
      showCancelButton: true,
      confirmButtonText: isEdit ? "Yes, update" : "Yes, save",
      cancelButtonText: "Cancel"
    });
    if (!confirm.isConfirmed) return;

    try {
      setLoading(true);
      const { vendor_code, ...rest } = vendor;
      const res = isEdit
        ? await vendorsService.update(editVendorId, rest)
        : await vendorsService.create({ ...rest, ...(vendor_code.trim() ? { vendor_code: vendor_code.trim() } : {}) });
      if (res?.success) {
        await withReactContent(Swal).fire({ title: "Success", text: res.message, icon: "success" });
        if (isEdit) {
          navigate("/vendor/list");
        } else {
          setVendor(initialVendor);
          setCityOptions([]);
          setStateOptions([]);
        }
      } else {
        toast.error(res?.message);
      }
    } catch (err) {
      showSwal("Error", SOMETHING_WENT_WRONG, "error")
    } finally {
      setLoading(false);
    }
  }

  if (loading) return (<ContentLoader loaderType="circleSpin" />)

  const labelStyle = formLabelStyle;
  const inputStyle = formInputStyle;
  const readOnlyStyle = formReadOnlyStyle;
  const selectStyles = formSelectStyles;
  const sectionCard = formSectionCard;
  const sectionTitle = formSectionTitle;
  const field = (label, required, node, cls = "col-12 col-md-6 col-lg-4") => formField(label, required, node, cls);
  const title = isEdit ? "Update Vendor" : "Add Vendor";

  return (
    <>
      <section className="section dashboard">
        <div className="d-flex align-items-start justify-content-between flex-wrap gap-2 mb-3">
          <div>
            <h4 className="m-0 fw-bold" style={{ color: "var(--page-heading)", fontSize: 20 }}>{isEdit ? "Update Vendor" : "Add New Vendor"}</h4>
            <nav className="d-flex align-items-center gap-1 mt-1" style={{ fontSize: 12, color: "#475569" }}>
              <Link to="/dashboard" style={{ color: "#475569" }}><i className="bi bi-house-door"></i></Link>
              <i className="bi bi-chevron-right" style={{ fontSize: 9 }}></i>
              <Link to="/vendor/list" style={{ color: "#475569" }}>Vendors</Link>
              <i className="bi bi-chevron-right" style={{ fontSize: 9 }}></i>
              <span className="fw-semibold" style={{ color: "#4a5568" }}>{title}</span>
            </nav>
          </div>
          <Link to="/vendor/list" className="btn btn-sm d-flex align-items-center gap-1 text-white px-3"
            style={{ background: "var(--btn-bg)", borderRadius: 8, height: 34, fontSize: 13, fontWeight: 600 }}>
            <i className="bi bi-list-ul"></i> Vendors List
          </Link>
        </div>

        <form className="v2a-form" onSubmit={handleSubmit}>

            <div style={sectionCard}>
              {sectionTitle("bi-building", "Company Detail", "Registered business information")}
              <div className="row g-3">
                {field("Company Name", true,
                  <input type="text" className="form-control" placeholder="e.g. Sky Travels Pvt Ltd" name="company_name"
                    value={vendor.company_name} onChange={handleChange} style={inputStyle} />
                )}
                {field("Vendor Code", false,
                  <input type="text" className="form-control" placeholder={isEdit ? "" : "Auto-generated if left blank"} name="vendor_code"
                    value={vendor.vendor_code} onChange={handleChange} readOnly={isEdit}
                    style={{ ...(isEdit ? readOnlyStyle : inputStyle), textTransform: "uppercase" }} />
                )}
                {field("Status", false,
                  <Select
                    options={STATUS_OPTIONS}
                    value={STATUS_OPTIONS.find(o => o.value === vendor.status) || null}
                    onChange={(o) => setVendor({ ...vendor, status: o ? o.value : "active" })}
                    menuPortalTarget={document.body}
                    styles={selectStyles}
                  />
                )}
              </div>
            </div>

            <div style={sectionCard}>
              {sectionTitle("bi-person-lines-fill", "Contact Detail", "Primary contact person for this vendor")}
              <div className="row g-3">
                {field("Executive", true,
                  <input type="text" className="form-control" placeholder="Contact person name" name="executive"
                    value={vendor.executive} onChange={handleChange} style={inputStyle} />
                )}
                {field("Mobile", true,
                  <input type="text" inputMode="numeric" className="form-control" placeholder="10 digit mobile number" name="mobile"
                    value={vendor.mobile} onChange={handleMobileChange} style={inputStyle} />
                )}
                {field("Email", true,
                  <input type="email" className="form-control" placeholder="name@company.com" name="email"
                    value={vendor.email} onChange={handleChange} style={inputStyle} />
                )}
              </div>
            </div>

            <div style={sectionCard}>
              {sectionTitle("bi-geo-alt", "Address Detail", "Registered office address")}
              <div className="row g-3">
                {field("Address", true,
                  <input type="text" className="form-control" placeholder="Street, area, landmark" name="address"
                    value={vendor.address} onChange={handleChange} style={inputStyle} />,
                  "col-12"
                )}
                {field("Pincode", true,
                  <input type="text" inputMode="numeric" className="form-control" placeholder="6 digit pincode" name="pincode"
                    value={vendor.pincode} onChange={handlePincodeChange} style={inputStyle} />
                )}
                {field("City", true,
                  cityOptions.length > 1 ? (
                    <Select
                      options={cityOptions}
                      value={vendor.city ? { value: vendor.city, label: vendor.city } : null}
                      onChange={(o) => setVendor({ ...vendor, city: o ? o.value : "" })}
                      placeholder="Select city"
                      menuPortalTarget={document.body}
                      styles={selectStyles}
                    />
                  ) : (
                    <input type="text" className="form-control" placeholder="City" name="city"
                      value={vendor.city} onChange={handleChange} style={inputStyle} />
                  )
                )}
                {field("State", true,
                  stateOptions.length > 1 ? (
                    <Select
                      options={stateOptions}
                      value={vendor.state ? { value: vendor.state, label: vendor.state } : null}
                      onChange={(o) => setVendor({ ...vendor, state: o ? o.value : "" })}
                      placeholder="Select state"
                      menuPortalTarget={document.body}
                      styles={selectStyles}
                    />
                  ) : (
                    <input type="text" className="form-control" placeholder="State" name="state"
                      value={vendor.state} onChange={handleChange} style={inputStyle} />
                  )
                )}
                {field("Country", true,
                  <input type="text" className="form-control" placeholder="Country" name="country"
                    value={vendor.country} onChange={handleChange} style={inputStyle} />
                )}
              </div>
            </div>

            <div className="d-flex justify-content-end gap-2">
              <Link to="/vendor/list" className="btn px-4 d-flex align-items-center"
                style={{ background: "#fff", border: "1px solid #c3cad6", borderRadius: 8, height: 40, fontSize: 13, color: "#444" }}>
                Back
              </Link>
              <button type="submit" className="btn px-4 text-white fw-semibold"
                style={{ background: GRAD, border: "none", borderRadius: 8, height: 40, fontSize: 13 }}>
                {title}
              </button>
            </div>

        </form>
      </section>
    </>
  );
}

export default AddVendor;
